import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ReleaseNotes: React.FC = () => {
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the markdown file
    fetch('/ressources/release-notes.md')
      .then(response => response.text())
      .then(text => {
        setMarkdownContent(text);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading release notes:', error);
        setMarkdownContent('# Error\n\nUnable to load release notes.');
        setLoading(false);
      });
  }, []);

  return (
    <div className="text-gray-100 flex flex-col">
      
      <main className="flex-grow w-full max-w-5xl mx-auto p-4 md:p-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
            <BookOpen className="w-8 h-8 text-blue-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Release Notes
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Latest updates and improvements to the platform
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8">
          {loading ? (
            <div className="text-center text-gray-400 py-8">Loading release notes...</div>
          ) : (
            <div className="prose prose-invert max-w-none release-notes-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdownContent}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .release-notes-content h1 {
          font-size: 2rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          margin-top: 0;
          color: #fff;
        }
        
        .release-notes-content h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
          margin-top: 2rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #374151;
          color: #e5e7eb;
        }
        
        .release-notes-content h3 {
          font-size: 1.25rem;
          font-weight: 500;
          margin-bottom: 0.75rem;
          margin-top: 1.5rem;
          color: #d1d5db;
        }
        
        .release-notes-content p {
          margin-bottom: 1rem;
          line-height: 1.7;
          color: #9ca3af;
        }
        
        .release-notes-content ul,
        .release-notes-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
          color: #9ca3af;
        }
        
        .release-notes-content li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }
        
        .release-notes-content code {
          background-color: rgba(55, 65, 81, 0.5);
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
          font-family: 'Courier New', monospace;
          color: #fbbf24;
        }
        
        .release-notes-content pre {
          background-color: rgba(31, 41, 55, 0.5);
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1rem;
        }
        
        .release-notes-content pre code {
          background-color: transparent;
          padding: 0;
          color: #d1d5db;
        }
        
        .release-notes-content a {
          color: #60a5fa;
          text-decoration: none;
          transition: color 0.2s;
        }
        
        .release-notes-content a:hover {
          color: #93c5fd;
          text-decoration: underline;
        }
        
        .release-notes-content hr {
          margin: 2rem 0;
          border: none;
          border-top: 1px solid #374151;
        }
        
        .release-notes-content blockquote {
          border-left: 4px solid #60a5fa;
          padding-left: 1rem;
          margin-left: 0;
          font-style: italic;
          color: #9ca3af;
        }
        
        .release-notes-content strong {
          font-weight: 600;
          color: #e5e7eb;
        }
        
        .release-notes-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1rem;
        }
        
        .release-notes-content th,
        .release-notes-content td {
          padding: 0.5rem;
          border: 1px solid #374151;
          text-align: left;
        }
        
        .release-notes-content th {
          background-color: rgba(55, 65, 81, 0.5);
          font-weight: 600;
          color: #e5e7eb;
        }
        
        .release-notes-content td {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default ReleaseNotes;
